import { useFeatureFlags } from '@fs/zion-flags'
import { ContentAttach, NoticeLoading } from '@fs/zion-icon'
import { useSlsStateContext } from '@fs/zion-sls'
import {
  Alert,
  Button,
  colors,
  QuickGlanceActions,
  QuickGlanceOverlay,
  H2,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  Paragraph,
  Row,
  Separator,
  useOverlay,
} from '@fs/zion-ui'
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { errorCollector } from '../../../../../shared/utils/utils'
import hasMeaningfulContent from '../../utils/hasMeaningfulContent'
import useGenealogistQuestion from '../../hooks/useGenealogistQuestion'
import PersonElement from './PersonElement'
import {
  createPersonExtractionMessages,
  createRelationshipLinkingMessages,
  extractAndParseJson,
} from './AIPersonsExpander.utils'

const AIPersonsExpander = ({ plainTextTranscript }) => {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const overlay = useOverlay()

  // Initialize the genealogist question hooks
  // One question will be asked right after the other
  const {
    loading: loadingPersonExtraction,
    answer: personExtractionAnswer,
    error: personExtractionError,
    askQuestion: extractPersons,
  } = useGenealogistQuestion()

  const {
    loading: loadingRelationshipLinking,
    answer: relationshipLinkingAnswer,
    error: relationshipLinkingError,
    askQuestion: linkRelationships,
  } = useGenealogistQuestion()

  const { rip_sharedImageViewer_ShowPromptDetails: socialStarFlag } = useFeatureFlags([
    'rip_sharedImageViewer_ShowPromptDetails',
  ])

  const currentImageArk = useMemo(() => state.currentImageArk, [state.currentImageArk])

  const [aiGeneratedPersons, setAiGeneratedPersons] = useState([])
  const [isAiPersonsExpanderOpen, setIsAiPersonsExpanderOpen] = useState(false)
  const [summaryError, setSummaryError] = useState(false)
  const [summarization] = useState('Here is the response we get from OpenAI')

  // Determine if any AI operation is loading
  const loadingAiPersons = loadingPersonExtraction || loadingRelationshipLinking

  const handleSummarizeClick = (e) => {
    e.stopPropagation()
    if (socialStarFlag.isOn) {
      overlay.open({ target: e.currentTarget })
    }
  }

  const fetchAiPersons = useCallback(async () => {
    try {
      const noMeaningfulContent = !hasMeaningfulContent(plainTextTranscript)
      const incompleteData = !plainTextTranscript || !currentImageArk
      if (noMeaningfulContent || incompleteData) {
        setAiGeneratedPersons([])
        return
      }

      // First call for person extraction
      const personExtractionMessages = createPersonExtractionMessages(plainTextTranscript)
      await extractPersons(personExtractionMessages)
    } catch (error) {
      setSummaryError(true)
      errorCollector(error, currentImageArk, {
        codeId: 30,
      })
    }
  }, [extractPersons, plainTextTranscript, currentImageArk])

  // Handle person extraction response
  useEffect(() => {
    if (personExtractionAnswer && !loadingPersonExtraction) {
      const parsedData = extractAndParseJson(personExtractionAnswer)

      if (parsedData) {
        // Second call for relationship linking
        const relationshipMessages = createRelationshipLinkingMessages(plainTextTranscript, parsedData)
        linkRelationships(relationshipMessages)
      } else {
        setAiGeneratedPersons([])
      }
    }
  }, [personExtractionAnswer, loadingPersonExtraction, plainTextTranscript, linkRelationships])

  // Handle relationship linking response
  useEffect(() => {
    if (relationshipLinkingAnswer && !loadingRelationshipLinking) {
      const finalParsedData = extractAndParseJson(relationshipLinkingAnswer)
      setAiGeneratedPersons(finalParsedData || [])
    }
  }, [relationshipLinkingAnswer, loadingRelationshipLinking])

  // Handle errors
  useEffect(() => {
    if (personExtractionError || relationshipLinkingError) {
      setSummaryError(true)
      const error = personExtractionError || relationshipLinkingError
      errorCollector(error, currentImageArk, {
        codeId: 30,
      })
    }
  }, [personExtractionError, relationshipLinkingError, currentImageArk])

  useEffect(() => {
    if (!isAiPersonsExpanderOpen) return
    fetchAiPersons()
  }, [fetchAiPersons, isAiPersonsExpanderOpen])

  // Reset state when current image changes
  useEffect(() => {
    setIsAiPersonsExpanderOpen(false)
    setAiGeneratedPersons([])
    setSummaryError(false)
  }, [currentImageArk])

  const retryFetch = () => {
    setSummaryError(false)
    fetchAiPersons()
  }

  return (
    <Expander
      open={isAiPersonsExpanderOpen && currentImageArk === state.currentImageArk}
      setOpen={(value) => setIsAiPersonsExpanderOpen(value)}
      linkName="full-text-summarize-expander"
    >
      <ExpanderHeader>
        <Separator size="nano" />
        <Row alignItemsY="middle" guttersY="lg">
          <ContentAttach color={colors.blue60} onClick={handleSummarizeClick} />
          <strong style={{ color: colors.blue60 }}>{t('full-text.ai-records-header', 'Connect people to tree')}</strong>
        </Row>
        <Separator size="nano" />
      </ExpanderHeader>
      <QuickGlanceOverlay {...overlay} title={t('full-text.prompt-title', 'Prompt sent to ChatGPT')}>
        <Paragraph size="sm">{summarization}</Paragraph>
        <QuickGlanceActions>
          <Button
            onClick={overlay.close}
            keyline="end"
            data-testid="close-quick-glance-button"
            linkName="close-quick-glance"
          >
            {t('full-text.close-button', 'Close')}
          </Button>
        </QuickGlanceActions>
      </QuickGlanceOverlay>
      <ExpanderContent>
        {loadingAiPersons ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <NoticeLoading />
          </div>
        ) : (
          <>
            {aiGeneratedPersons && aiGeneratedPersons.length > 0 && (
              <>
                <H2 size="H5">{t('full-text.ai-generated-persons', 'AI-Generated Persons')}</H2>
                <Separator size="xs" />
                <PersonElement persons={aiGeneratedPersons} />
                <Separator size="xs" />
              </>
            )}
            {summaryError && (
              <>
                <Alert type="warning">
                  {t(
                    'full-text.summary-error',
                    'We encountered an error creating the summary. Please try again later.'
                  )}
                </Alert>
                <Separator size="xs" />
                <Button emphasis="medium" onClick={retryFetch}>
                  {t('full-text.summary-error-retry', 'Try Again')}
                </Button>
              </>
            )}
          </>
        )}
        <Separator size="xs" />
      </ExpanderContent>
    </Expander>
  )
}

export default AIPersonsExpander
